import { Collapsible } from "@base-ui/react/collapsible"
import React, { useState } from "react"
import { Controller, useFormContext } from "react-hook-form"

import Button from "src/components/Button"
import Form from "src/components/Form"

import DimensionInput from "./DimensionInput"

const BOAT_TYPES = [
  { value: "power", label: "Power" },
  { value: "sail", label: "Sail" },
]

const BoatFieldGroup = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const boatErrors = errors?.contact_boat || {}
  const [moreOpen, setMoreOpen] = useState(false)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <Form.Label htmlFor="inline-boat-name" required>
            Boat Name
          </Form.Label>
          <Controller
            name="contact_boat.name"
            control={control}
            rules={{ required: "Boat name is required" }}
            render={({ field }) => (
              <Form.TextField
                id="inline-boat-name"
                hasErrors={!!boatErrors.name}
                required
                {...field}
              />
            )}
          />
          {boatErrors.name && (
            <Form.Error>{boatErrors.name.message}</Form.Error>
          )}
        </div>

        <div className="flex-1">
          <Controller
            name="contact_boat.boat_type"
            control={control}
            rules={{ required: "Boat type is required" }}
            render={({ field: { onChange, value } }) => (
              <Form.Select.Custom
                id="inline-boat-type"
                formLabel="Boat Type"
                hasErrors={!!boatErrors.boat_type}
                onChange={onChange}
                required
                value={value}
              >
                <Form.Select.RichOption value="">
                  Select a type…
                </Form.Select.RichOption>
                {BOAT_TYPES.map((type) => (
                  <Form.Select.RichOption key={type.value} value={type.value}>
                    {type.label}
                  </Form.Select.RichOption>
                ))}
              </Form.Select.Custom>
            )}
          />
          {boatErrors.boat_type && (
            <Form.Error>{boatErrors.boat_type.message}</Form.Error>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="flex-1">
          <Controller
            name="contact_boat.length_overall"
            control={control}
            rules={{ validate: (v) => v > 0 || "LOA is required" }}
            render={({ field }) => (
              <DimensionInput
                id="inline-boat-loa"
                label="Length Overall"
                required
                value={field.value}
                onChange={field.onChange}
                error={boatErrors.length_overall?.message}
              />
            )}
          />
        </div>
        <div className="flex-1">
          <Controller
            name="contact_boat.beam"
            control={control}
            rules={{ validate: (v) => v > 0 || "Beam is required" }}
            render={({ field }) => (
              <DimensionInput
                id="inline-boat-beam"
                label="Beam"
                required
                value={field.value}
                onChange={field.onChange}
                error={boatErrors.beam?.message}
              />
            )}
          />
        </div>
      </div>

      <Collapsible.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <Collapsible.Trigger
          render={(props) => (
            <Button variant="link" small {...props}>
              {moreOpen ? "− Less details" : "+ More details"}
            </Button>
          )}
        />
        <Collapsible.Panel>
          <div className="flex flex-col gap-3 pt-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex-1">
                <Controller
                  name="contact_boat.draw"
                  control={control}
                  render={({ field }) => (
                    <DimensionInput
                      id="inline-boat-draft"
                      label="Draft"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <Controller
                  name="contact_boat.height"
                  control={control}
                  render={({ field }) => (
                    <DimensionInput
                      id="inline-boat-height"
                      label="Height"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex-1">
                <Form.Label htmlFor="inline-boat-make">Make</Form.Label>
                <Controller
                  name="contact_boat.make"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField id="inline-boat-make" {...field} />
                  )}
                />
              </div>
              <div className="flex-1">
                <Form.Label htmlFor="inline-boat-model">Model</Form.Label>
                <Controller
                  name="contact_boat.model"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField id="inline-boat-model" {...field} />
                  )}
                />
              </div>
              <div className="flex-1">
                <Form.Label htmlFor="inline-boat-year">Year</Form.Label>
                <Controller
                  name="contact_boat.year"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^\d{4}$/,
                      message: "Year must be 4 digits",
                    },
                  }}
                  render={({ field: { onChange, value, ...rest } }) => (
                    <Form.TextField
                      id="inline-boat-year"
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      hasErrors={!!boatErrors.year}
                      value={value ?? ""}
                      onChange={(e) =>
                        onChange(e.target.value.replace(/\D/g, "").slice(0, 4))
                      }
                      {...rest}
                    />
                  )}
                />
                {boatErrors.year && (
                  <Form.Error>{boatErrors.year.message}</Form.Error>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex-1">
                <Form.Label htmlFor="inline-boat-hailing-port">
                  Hailing Port
                </Form.Label>
                <Controller
                  name="contact_boat.hailing_port"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField id="inline-boat-hailing-port" {...field} />
                  )}
                />
              </div>
              <div className="flex-1">
                <Form.Label htmlFor="inline-boat-registration-number">
                  Registration Number
                </Form.Label>
                <Controller
                  name="contact_boat.registration_attributes.number"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField
                      id="inline-boat-registration-number"
                      {...field}
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <Form.Label htmlFor="inline-boat-hin">Hull ID (HIN)</Form.Label>
                <Controller
                  name="contact_boat.hull_identification"
                  control={control}
                  render={({ field }) => (
                    <Form.TextField id="inline-boat-hin" {...field} />
                  )}
                />
              </div>
            </div>
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>
    </div>
  )
}

export default BoatFieldGroup
